import { CalendarDate, Time } from '@internationalized/date';
import moment from 'moment-timezone';
import { DateRange } from 'v5/platform/lang/dateRange';

/**
 * Backend-oriented types for callsheet creation API
 * These types match the backend API format (snake_case, string dates, etc.)
 *
 * ## Objection Graph References Pattern (Preferred)
 *
 * Callsheets use Objection.js graph syntax with `#id` and `#ref`:
 *
 * - **Groups are defined at root level** with `#id` for new groups
 * - **Sections reference groups** via `#ref` in their `groups` array
 * - **Team members are at root level** and reference groups via `group: {#ref: "..."}`
 *
 * ### Key Principles (Preferred Pattern):
 * 1. Groups are defined at root level in `groups: [...]` with `#id`
 * 2. Sections reference groups using `#ref` in `sections[].groups: [{#ref: "group-id"}]`
 * 3. Team members reference groups via `group: {#ref: "group-id"}`
 * 4. Use `#id` for new groups at root level
 * 5. Use `#ref` to reference groups in sections and team_members
 * 6. `entity_type` is automatically set to CALLSHEET (5) by backend hook
 *
 * ### Alternative Pattern (Not Preferred):
 * Groups can also be nested within sections, but the preferred pattern is root-level groups.
 */

// Production reference (many-to-many)
export interface ProductionReference {
  id: number;
}

/**
 * Graph reference - can be an ID or a #ref
 */
export interface GraphRef {
  '#ref'?: string; // Reference to a group's #id
  id?: number; // Reference to existing group by database ID
}

// Callsheet Group (preferred: at root level)
export interface CreateCallsheetGroup {
  '#id'?: string; // For new groups - temporary ID for graph reference
  id?: number; // For existing groups - database ID
  name?: string; // Required when creating new group
  order?: number;
  // entity_type is automatically set to 5 (CALLSHEET) by backend hook
}

// Callsheet Section (references groups via #ref)
export interface CreateCallsheetSection {
  '#id'?: string; // For new sections - temporary ID for graph reference
  id?: number; // For existing sections - database ID
  name: string;
  order?: number;
  metadata?: Record<string, unknown>;
  groups?: GraphRef[]; // Array of group references: {id: number} or {#ref: "group-id"}
  // Note: Groups are defined at root level, sections reference them via #ref
}

// Callsheet Team Member (always at root level, references group)
export interface CreateCallsheetTeamMember {
  id?: number; // For existing team members - database ID
  contact_id: number;
  group?: GraphRef; // Group reference: {id: number} or {#ref: "group-id"}
  order?: number;
  display_name?: string | null;
  display_phone_number?: string | null;
  display_role?: string | null;
  display_profile_picture?: string | null;
  display_note?: string | null;
  note?: string | null;
  contact_details_visible?: boolean;
  call_time?: string | null; // Time format: "HH:mm:ss"
  production_id?: number;
  display_purchase_order_number?: string | null;
  recipients?: CreateCallsheetRecipient[];
}

// Callsheet Recipient
export interface CreateCallsheetRecipient {
  contact_id?: number | null;
  alt_name?: string | null;
  email_address?: string | null;
  callsheet_team_member_id?: number | null;
}

// Callsheet Attachment (Document)
export interface CreateCallsheetAttachment {
  file: {
    id: number;
  };
}

// Main backend request type
export interface CreateCallsheetRequest {
  // Required fields
  timezone: string; // e.g., "Europe/London", "America/New_York"
  production_date: string; // ISO 8601 date-time format: "2024-01-15T18:00:00Z"

  // Optional core fields
  title?: string | null;
  description?: string | null;
  public_title?: string | null;
  public_description?: string | null;
  status?: string | null; // Default: "BUILDING"
  from_date?: string; // Date format: "YYYY-MM-DD"
  to_date?: string | null; // Date format: "YYYY-MM-DD"
  call_time?: string | null; // Time format: "HH:mm:ss" (e.g., "12:00:00")
  note?: string | null;
  client_logo?: string | null;
  billing?: string | null;
  social?: string | null;
  hard_drive?: string | null;

  // Production relationships
  production_id?: number | null; // Single production (backward compatibility, legacy)
  // Note: DO NOT use production_ids - it's not a valid field. Use linked_productions instead.
  linked_productions?: ProductionReference[]; // Many-to-many relationship (preferred)

  // Status and visibility
  public?: boolean; // Default: true
  published?: boolean; // Default: false
  enable_notifications?: boolean | null; // Default: false

  // Metadata
  user_id?: string | null; // Owner/creator ID (auto-set if not provided)
  created_mechanism?: string | null;
  isClone?: boolean;

  // Preferred structure: all entities at root level
  groups?: CreateCallsheetGroup[]; // Groups at root level with #id (preferred pattern)
  sections?: CreateCallsheetSection[]; // Sections reference groups via #ref in groups array
  team_members?: CreateCallsheetTeamMember[]; // Team members at root level, reference groups via group: {#ref}
  recipients?: CreateCallsheetRecipient[];
  attachments?: CreateCallsheetAttachment[];

  // Array fields (stored as JSONB)
  points_of_contact?: Array<Record<string, any>> | null;
  locations?: Array<Record<string, any>> | null;
  schedule?: Array<Record<string, any>> | null;
}

/**
 * Convert CalendarDate to YYYY-MM-DD format
 */
export function calendarDateToString(date: CalendarDate): string {
  const year = date.year;
  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert Time to HH:mm:ss format
 */
export function timeToString(time: Time): string {
  const hour = String(time.hour).padStart(2, '0');
  const minute = String(time.minute).padStart(2, '0');
  return `${hour}:${minute}:00`;
}

/**
 * Convert frontend CreateCallsheetInput to backend CreateCallsheetRequest
 */
export function convertCreateCallsheetInputToRequest(
  input: CreateCallsheetInput
): CreateCallsheetRequest {
  const timezone = moment.tz.guess();

  // Determine from_date and to_date based on dateSelection
  let fromDate: string | null = null;
  let toDate: string | null = null;

  if (input.dateSelection?.type === 'range') {
    fromDate = calendarDateToString(input.dateSelection.from);
    toDate = calendarDateToString(input.dateSelection.to);
  } else if (input.dateSelection?.type === 'single') {
    fromDate = calendarDateToString(input.dateSelection.date);
  } else if (input.date) {
    // Backward compatibility for deprecated date field
    fromDate = calendarDateToString(input.date);
  }

  const callTime = input.generalCallTime
    ? timeToString(input.generalCallTime)
    : null;

  const baseRequest: CreateCallsheetRequest = {
    title: input.title || null,
    production_id:
      input.selectedProductionIds.length > 0
        ? input.selectedProductionIds[0]
        : null,
    linked_productions: input.selectedProductionIds.map(id => ({ id })),
    from_date: fromDate ?? undefined,
    to_date: toDate ?? undefined,
    call_time: callTime,
    timezone,
    production_date: moment().toISOString(),
    // Optional fields for duplication
    description: input.description,
    note: input.note,
    billing: input.billing,
    social: input.social,
    hard_drive: input.hard_drive,
    client_logo: input.client_logo,
    schedule: input.schedule,
    points_of_contact: input.points_of_contact,
    attachments: input.attachments,
    isClone: input.isClone,
    ...input.generatedData
  };

  return baseRequest;
}

/**
 * Generated callsheet data from productions
 *
 * Uses preferred structure with Objection graph references:
 * - Groups are defined at root level with #id
 * - Sections reference groups via #ref in their groups array
 * - Team members are at root level and reference groups via group: {#ref}
 */
export interface GeneratedCallsheetData {
  groups?: CreateCallsheetGroup[]; // Groups at root level with #id
  sections?: CreateCallsheetSection[]; // Sections with groups: [{#ref: "group-id"}]
  team_members?: CreateCallsheetTeamMember[]; // Team members with group: {#ref: "group-id"}
  locations?: Array<Record<string, any>>; // Locations collected from confirmed location slots
  recipients?: CreateCallsheetRecipient[];
}

/**
 * Frontend-oriented input type for callsheet creation
 * This is the simple, user-friendly type that matches the form data
 */
export type CreateCallsheetDateSelection =
  | { type: 'single'; date: CalendarDate }
  | ({ type: 'range' } & DateRange);

export interface CreateCallsheetInput {
  selectedProductionIds: number[];
  title: string;
  dateSelection?: CreateCallsheetDateSelection;
  /** @deprecated Use dateSelection instead */
  date?: CalendarDate;
  generalCallTime: Time | null;
  generatedData?: GeneratedCallsheetData;

  // Optional fields for duplication - these are copied from original callsheet
  /** Description/outline of the callsheet */
  description?: string | null;
  /** Notes for the callsheet */
  note?: string | null;
  /** Billing information */
  billing?: string | null;
  /** Social media information */
  social?: string | null;
  /** Hard drive information */
  hard_drive?: string | null;
  /** Client logo URL */
  client_logo?: string | null;
  /** Schedule entries */
  schedule?: Array<Record<string, any>> | null;
  /** Points of contact */
  points_of_contact?: Array<Record<string, any>> | null;
  /** Attachments (without IDs for new callsheet) */
  attachments?: CreateCallsheetAttachment[];
  /** Set when duplicating — backend skips default external recipients from settings */
  isClone?: boolean;
}
